import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import type { WheelItem } from "../../../types";

// Lightweight haptic nicety, feature-detected - same helper as
// GameStage.tsx's buzz(), duplicated rather than shared since it's a
// two-line function and the two features don't otherwise share a module.
function buzz(ms: number): void {
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    navigator.vibrate(ms);
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

// Four-tone repeating ramp so adjacent wedges never share a color even with
// 24 segments, entirely within the existing pink/plum brand ramp - no new
// hues introduced.
const SEGMENT_COLORS = ["var(--pink-400)", "var(--pink-600)", "#7a3155", "var(--pink-500)"];

const Dial = styled.div<{ $background: string; $transitionMs: number; $rotation: number }>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow:
    var(--shadow),
    inset 0 0 0 4px var(--surface-opaque);
  background: ${(p) => p.$background};
  transform: rotate(${(p) => p.$rotation}deg);
  transition: ${(p) => (p.$transitionMs > 0 ? `transform ${p.$transitionMs}ms cubic-bezier(0.12, 0.67, 0.1, 1)` : "none")};
`;

const SegmentIcon = styled.i<{ $angle: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  transform: translate(-50%, -50%) rotate(${(p) => p.$angle}deg) translateY(-42%);
  pointer-events: none;
`;

export function WheelCanvas({
  items,
  resultId,
  spinNumber,
  disabled,
  onSpin,
}: {
  items: WheelItem[];
  resultId: string | null;
  spinNumber: number;
  disabled: boolean;
  onSpin: () => void;
}) {
  const dialRef = useRef<HTMLDivElement>(null);
  const rotationRef = useRef(0);
  const lastSpinNumberRef = useRef<number | null>(null);
  const tickCleanupRef = useRef<(() => void) | null>(null);
  const [rotation, setRotation] = useState(0);
  const [transitionMs, setTransitionMs] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const segAngle = items.length > 0 ? 360 / items.length : 0;

  // Fixed visual order (items never reshuffle client-side) - only the
  // rotation target changes per spin, so the wheel face itself stays
  // stable and only the pointer's landing position moves.
  const background =
    items.length > 0
      ? `conic-gradient(from 0deg, ${items
          .map((_, i) => `${SEGMENT_COLORS[i % SEGMENT_COLORS.length]} ${i * segAngle}deg ${(i + 1) * segAngle}deg`)
          .join(", ")})`
      : "var(--surface-soft)";

  useEffect(() => {
    return () => tickCleanupRef.current?.();
  }, []);

  useEffect(() => {
    if (!resultId || items.length === 0) return;
    const idx = items.findIndex((it) => it.id === resultId);
    if (idx === -1) return;

    const centerAngle = (idx + 0.5) * segAngle;
    const targetMod = (360 - (centerAngle % 360)) % 360;

    if (lastSpinNumberRef.current === null) {
      // First mount (or a reconnect resync) - jump straight to the result,
      // no animation. Replaying a multi-second spin animation after a
      // refresh would be re-litigating a result the server already
      // decided, not showing something new.
      rotationRef.current = targetMod;
      setTransitionMs(0);
      setRotation(targetMod);
      lastSpinNumberRef.current = spinNumber;
      return;
    }

    if (spinNumber === lastSpinNumberRef.current) return;

    const reduced = prefersReducedMotion();
    const prevMod = ((rotationRef.current % 360) + 360) % 360;
    let delta = targetMod - prevMod;
    if (delta <= 0) delta += 360;
    const extraSpins = reduced ? 0 : 4;
    const duration = reduced ? 350 : 3800;
    const newRotation = rotationRef.current + extraSpins * 360 + delta;

    tickCleanupRef.current?.();
    setSpinning(true);
    setTransitionMs(duration);
    setRotation(newRotation);
    rotationRef.current = newRotation;
    lastSpinNumberRef.current = spinNumber;

    if (reduced) {
      const t = setTimeout(() => {
        buzz(30);
        setSpinning(false);
      }, duration);
      tickCleanupRef.current = () => clearTimeout(t);
    } else {
      tickCleanupRef.current = runTickLoop(dialRef.current, segAngle, duration, () => setSpinning(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultId, spinNumber, items.length]);

  return (
    <>
      <div className="wheel-pointer" aria-hidden="true">
        <i className="bi bi-caret-down-fill" />
      </div>
      <Dial ref={dialRef} $background={background} $rotation={rotation} $transitionMs={transitionMs} role="img" aria-label="Couple Wheel">
        {items.map((item, i) => (
          <SegmentIcon key={item.id} className={`bi ${item.icon}`} $angle={(i + 0.5) * segAngle} aria-hidden="true" />
        ))}
      </Dial>
      <button type="button" className="wheel-spin-btn" onClick={onSpin} disabled={disabled || spinning}>
        {spinning ? <i className="bi bi-arrow-repeat" aria-hidden="true" /> : "Spin"}
      </button>
    </>
  );
}

// Watches the dial's live computed rotation (rather than replicating the
// CSS easing curve's math) and fires a short buzz each time it crosses a
// segment boundary, plus one firmer pulse when the transition ends -
// robust to whatever easing curve the transition above actually uses.
function runTickLoop(dialEl: HTMLDivElement | null, segAngle: number, durationMs: number, onDone: () => void): () => void {
  if (!dialEl || segAngle <= 0) {
    onDone();
    return () => {};
  }
  let lastSegment = -1;
  let rafId = 0;
  const start = performance.now();

  function frame(now: number) {
    const elapsed = now - start;
    const style = dialEl ? getComputedStyle(dialEl) : null;
    let angle = 0;
    const match = style?.transform.match(/matrix\(([^)]+)\)/);
    if (match) {
      const [a, b] = match[1].split(",").map(Number);
      angle = Math.atan2(b, a) * (180 / Math.PI);
      if (angle < 0) angle += 360;
    }
    const segIndex = Math.floor(((360 - angle) % 360) / segAngle);
    if (segIndex !== lastSegment) {
      lastSegment = segIndex;
      buzz(8);
    }
    if (elapsed < durationMs) {
      rafId = requestAnimationFrame(frame);
    } else {
      buzz(30);
      onDone();
    }
  }
  rafId = requestAnimationFrame(frame);
  return () => cancelAnimationFrame(rafId);
}
